import React, { useContext, useState } from "react"
import { useFormContext } from "react-hook-form"

import Button from "src/components/Button"
import Form from "src/components/Form"
import Heading from "src/components/Heading"

import AddProductTaxForm from "./AddProductTaxForm"
import FormSection from "./FormSection"
import { ItemFormContext } from "./ItemFormContainer"
import ProductTaxRow from "./ProductTaxRow"
import { appliedTaxRate } from "./helpers"

const Taxes = () => {
  const { marinaTaxes, product } = useContext(ItemFormContext)
  const { watch, setValue } = useFormContext()
  const [showAddForm, setShowAddForm] = useState(false)

  const appliedTaxIds = watch("appliedTaxIds") || []
  const productTaxesAttributes = watch("productTaxesAttributes") || []

  if (!marinaTaxes || marinaTaxes.length === 0) {
    return null
  }

  const handleMarinaTaxChange = (taxId) => {
    const currentIds = appliedTaxIds
    const newIds = currentIds.includes(taxId)
      ? currentIds.filter((id) => id !== taxId)
      : [...currentIds, taxId]
    setValue("appliedTaxIds", newIds)
  }

  const handleAddProductTax = (newTax) => {
    setValue("productTaxesAttributes", [...productTaxesAttributes, newTax])
    setShowAddForm(false)
  }

  const handleDeleteProductTax = (index) => {
    const updated = [...productTaxesAttributes]
    const taxToDelete = updated[index]

    if (taxToDelete.id) {
      // Existing tax - mark for destruction
      updated[index] = { ...taxToDelete, _destroy: true }
    } else {
      // New tax (no id) - remove from array
      updated.splice(index, 1)
    }
    setValue("productTaxesAttributes", updated)
  }

  // Filter out destroyed taxes for display
  const visibleProductTaxes = productTaxesAttributes.filter((t) => !t._destroy)

  const combinedRate = appliedTaxRate({
    marinaTaxes,
    appliedTaxIds,
    productTaxesAttributes,
  })
  const combinedRateDisplay = `${(combinedRate * 100).toFixed(2)}%`

  return (
    <FormSection>
      <Heading.SectionHeader>Configure Taxes</Heading.SectionHeader>
      <div className="space-y-6 pt-4">
        {/* Marina Taxes Section */}
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <Heading.SubSectionHeader>Marina Taxes</Heading.SubSectionHeader>
          <p className="mb-3 text-sm text-gray-600">
            Applied by default. Uncheck to exclude.
          </p>
          <div className="flex flex-col space-y-2">
            {marinaTaxes.map((tax) => (
              <Form.Checkbox
                key={tax.id}
                name={`tax-${tax.id}`}
                checked={appliedTaxIds.includes(tax.id)}
                onChange={() => handleMarinaTaxChange(tax.id)}
                label={`${tax.name} (${tax.rateDisplay})`}
              />
            ))}
          </div>
        </div>

        {/* Product Taxes Section */}
        {product?.id && (
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <Heading.SubSectionHeader>
              Product-Specific Taxes
            </Heading.SubSectionHeader>
            <p className="mb-3 text-sm text-gray-600">
              Taxes that apply only to this product.
            </p>

            {visibleProductTaxes.length > 0 && (
              <div className="mb-3 divide-y divide-gray-200">
                {visibleProductTaxes.map((tax, visibleIndex) => {
                  // Find actual index in full array (including destroyed)
                  const actualIndex = productTaxesAttributes.findIndex(
                    (t) => t === tax
                  )
                  return (
                    <ProductTaxRow
                      key={tax.id || `new-${actualIndex}`}
                      tax={tax}
                      onDelete={() => handleDeleteProductTax(actualIndex)}
                    />
                  )
                })}
              </div>
            )}

            {!showAddForm ? (
              <Button
                onClick={() => setShowAddForm(true)}
                variant="tertiary"
                small
                icon="fa-plus"
              >
                Add Product Tax
              </Button>
            ) : (
              <AddProductTaxForm
                onSave={handleAddProductTax}
                onCancel={() => setShowAddForm(false)}
              />
            )}
          </div>
        )}

        {!product?.id && (
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
            <p className="text-sm italic text-gray-600">
              Save this product first to add product-specific taxes.
            </p>
          </div>
        )}

        {/* Combined Tax Rate Display */}
        {(visibleProductTaxes.length > 0 ||
          marinaTaxes.some((tax) => appliedTaxIds.includes(tax.id))) && (
          <div className="bg-gray-50 rounded-lg border border-gray-300 p-4">
            <div className="flex justify-between">
              <span className="font-semibold text-gray-900">
                Combined Tax Rate:
              </span>
              <span className="font-semibold text-gray-900">
                {combinedRateDisplay}
              </span>
            </div>
          </div>
        )}
      </div>
    </FormSection>
  )
}

export default Taxes
